import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // URLのhashやsearch paramsからセッションを取得
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          setStatus("error");
          setMessage(error.message);
          setTimeout(() => navigate("/"), 3000);
          return;
        }

        if (data.session) {
          setStatus("success");
          setTimeout(() => navigate("/"), 1500);
          return;
        }

        // exchangeCodeForSession でcodeを処理
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");

        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            setStatus("error");
            setMessage(exchangeError.message);
            setTimeout(() => navigate("/"), 3000);
          } else {
            setStatus("success");
            setTimeout(() => navigate("/"), 1500);
          }
          return;
        }

        // hashフラグメントの処理（#access_token=...）
        const hash = window.location.hash;
        if (hash && hash.includes("access_token")) {
          // Supabaseが自動でhashを処理するのでセッション再取得
          const { data: refreshed } = await supabase.auth.getSession();
          if (refreshed.session) {
            setStatus("success");
            setTimeout(() => navigate("/"), 1500);
            return;
          }
        }

        // セッションなし → ホームへ
        navigate("/");
      } catch {
        setStatus("error");
        setMessage("認証処理中にエラーが発生しました");
        setTimeout(() => navigate("/"), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="bg-[#0f1015] border border-white/10 rounded-2xl p-10 text-center max-w-sm w-full mx-4">
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent rounded-t-2xl"></div>

        {status === "loading" && (
          <>
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-400/10 rounded-full border border-amber-400/20">
              <span className="w-8 h-8 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin block"></span>
            </div>
            <h2 className="text-white text-lg font-black mb-2">認証処理中...</h2>
            <p className="text-gray-400 text-sm">しばらくお待ちください</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-emerald-400/10 rounded-full border border-emerald-400/20">
              <i className="ri-checkbox-circle-line text-emerald-400 text-3xl"></i>
            </div>
            <h2 className="text-white text-lg font-black mb-2">認証完了！</h2>
            <p className="text-gray-400 text-sm">ゲームへ移動します...</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-red-400/10 rounded-full border border-red-400/20">
              <i className="ri-error-warning-line text-red-400 text-3xl"></i>
            </div>
            <h2 className="text-white text-lg font-black mb-2">認証エラー</h2>
            <p className="text-gray-400 text-sm mb-2">{message}</p>
            <p className="text-gray-500 text-xs">ホームへ戻ります...</p>
          </>
        )}
      </div>
    </div>
  );
}
